import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * Native Valleans cart discount component.
 * Shopify remains the source of truth; both apply and remove actions use the
 * same Horizon declarative Component action system and re-render the section
 * returned by the Ajax Cart API.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError
 * @property {HTMLElement} cartDiscountErrorDiscountCode
 * @property {HTMLElement} cartDiscountErrorShipping
 *
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleEmptyCart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleEmptyCart);
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  #createAbortController() {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    return controller;
  }

  /** @param {CustomEvent} event */
  #handleEmptyCart = (event) => {
    const itemCount = event.detail?.data?.itemCount;
    if (itemCount !== 0 || typeof this.dataset.sectionId !== 'string') return;

    const source = event.target;
    if (!(source instanceof HTMLElement) || source.dataset.sectionId !== this.dataset.sectionId) return;

    void this.#updateDiscountCodes([]);
  };

  /** @param {SubmitEvent} event */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || typeof this.dataset.sectionId !== 'string') return;

    const input = form.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;

    const code = input.value.trim();
    if (!code) return;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.some((item) => item.toLowerCase() === code.toLowerCase())) {
      input.value = '';
      return;
    }

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const controller = this.#createAbortController();

    try {
      const data = await this.#requestDiscountUpdate([...existingDiscounts, code], controller.signal);
      const serverCodes = Array.isArray(data.discount_codes) ? data.discount_codes : [];
      const requested = serverCodes.find((discount) => String(discount.code || '').toLowerCase() === code.toLowerCase());

      if (requested && requested.applicable === false) {
        input.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml === 'string' && newHtml.trim()) {
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const renderedCodes = section
          ? Array.from(section.querySelectorAll('.cart-discount__pill'))
              .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
              .filter(Boolean)
          : [];

        if (
          requested?.applicable === true &&
          renderedCodes.length === existingDiscounts.length &&
          renderedCodes.every((renderedCode) => existingDiscounts.includes(renderedCode))
        ) {
          input.value = '';
          this.#handleDiscountError('shipping');
          return;
        }
      }

      input.value = '';
      await this.#renderConfirmedState(data);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[Valleans cart] Discount apply failed', error);
      this.#handleDiscountError('discount_code');
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Public Horizon action invoked by on:click="/removeDiscount".
   * @param {MouseEvent} event
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const target = event.currentTarget instanceof Element ? event.currentTarget : event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.cart-discount__pill-remove');
    const pill = button?.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const code = pill.dataset.discountCode;
    if (!code) return;

    const remaining = this.#existingDiscounts().filter((item) => item.toLowerCase() !== code.toLowerCase());
    const controller = this.#createAbortController();

    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const data = await this.#requestDiscountUpdate(remaining, controller.signal);
      await this.#renderConfirmedState(data);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[Valleans cart] Discount remove failed', error);
      if (button instanceof HTMLButtonElement && button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-remove:user-action', event);
    }
  };

  /** @param {string[]} codes */
  async #updateDiscountCodes(codes) {
    if (typeof this.dataset.sectionId !== 'string') return;
    const controller = this.#createAbortController();

    try {
      const data = await this.#requestDiscountUpdate(codes, controller.signal);
      await this.#renderConfirmedState(data);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[Valleans cart] Discount reset failed', error);
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * @param {string[]} codes
   * @param {AbortSignal} signal
   */
  async #requestDiscountUpdate(codes, signal) {
    if (typeof this.dataset.sectionId !== 'string') throw new Error('Cart section id missing');

    const body = JSON.stringify({
      discount: codes.join(','),
      sections: [this.dataset.sectionId],
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', { body }),
      signal,
    });

    const data = await response.json();
    if (!response.ok || data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : `Cart discount update failed (${response.status})`);
    }

    return data;
  }

  /** @param {Object} data */
  async #renderConfirmedState(data) {
    if (typeof this.dataset.sectionId !== 'string') return;

    const sectionId = this.dataset.sectionId;
    const newHtml = data.sections?.[sectionId];

    document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

    if (typeof newHtml === 'string' && newHtml.trim()) {
      await morphSection(sectionId, newHtml);
    }
  }

  /** @param {'discount_code' | 'shipping'} type */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /** @returns {string[]} */
  #existingDiscounts() {
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .map((pill) => (pill instanceof HTMLLIElement ? pill.dataset.discountCode : null))
      .filter((code) => typeof code === 'string' && code.length > 0);
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
